import { Alert, Grid, Typography } from '@mui/material';
import { t } from 'i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useCallback, useState } from 'react';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlledAutocompleteDropdownLazy } from 'src/shared/components/form/controlledAutocompleteDropdownLazy';
import { getResCause } from 'src/shared/services/formSettingService';
import dayjs from 'dayjs';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { updateResignMember } from 'src/shared/services/memberService';
import { toast } from 'react-toastify';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import * as yup from 'yup';
import { ResignMemberRequest } from '../models/resignMemberRequest';
import { MemberResignMemberConfirmDialog } from './memberResignMemberConfirmDialog';
import { useExtraMembershipLabel } from '../hooks/useExtraMembershipLabel';
import { useSchemaResignMember } from '../schemas/useSchemaResignMember';

type MemberResignMemberModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  memberId: number;
  // Vid utträde avslutas även medlemmens vänmedlemskap, och då ska rutan säga det.
  hasExtraMemberships?: boolean;
  onResignSuccess: () => void;
};

export const MemberResignMemberModal = (props: MemberResignMemberModalProps) => {
  const { modalOpen, onClose, memberId, hasExtraMemberships = false, onResignSuccess } = props;
  const isMounted = useIsMounted();
  const friendLabel = useExtraMembershipLabel();

  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const resignMemberSchema = useSchemaResignMember();
  type ResignMemberFormInterface = yup.InferType<typeof resignMemberSchema>;

  const formMethods = useForm<ResignMemberFormInterface>({
    resolver: yupResolver(resignMemberSchema),
    defaultValues: {
      date: dayjs(Date.now()).toDate(),
      cause: undefined,
    },
    mode: 'all',
  });

  const { handleSubmit } = formMethods;

  const onCloseHandler = () => {
    onClose();
  };

  const onSubmit = useCallback(
    async (data: ResignMemberFormInterface) => {
      ConsoleHelper.log('onsubmit', data);
      const dataToPut: ResignMemberRequest = {
        date: DateHelper.postDate(data.date),
        cause: data.cause?.value ?? '',
      };
      ConsoleHelper.log('dataToPut', dataToPut);

      try {
        if (isMounted()) {
          await updateResignMember(memberId, dataToPut);
          toast.success(t('member:exitMember.saveSuccessMessage'));
          onResignSuccess();
          setOpenConfirm(false);
        }
      } catch (error) {
        if (isMounted()) {
          setOpenConfirm(false);
          ConsoleHelper.log('error utträde', error);
        }
      }
    },
    [isMounted, memberId, onResignSuccess],
  );

  const onSaveHandler = useCallback(() => {
    handleSubmit(
      () => setOpenConfirm(true),
      (error) => ConsoleHelper.log('Validation Failed:', error),
    )();
  }, [handleSubmit]);

  const onSaveConfirmHandler = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={onCloseHandler}
      onSave={onSaveHandler}
      maxWidth='xs'
      showDialogActions
      padding={0}
      dialogTitle={t('member:exitMember.exitOfMembership')}
      dialogContent={
        <>
          <Typography sx={{ p: 2 }}>{`${t('member:exitMember.description')}`}</Typography>
          {hasExtraMemberships && (
            <Alert severity='info' sx={{ mx: 2 }}>
              {t('member:exitMember.extraMembershipsEnded', { label: friendLabel.toLowerCase() })}
            </Alert>
          )}
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container>
                <Grid item xs={12} px={2} pt={2}>
                  <ControlledDatePicker name='date' required label={t('event:formFields.date')} />
                </Grid>
                <Grid item xs={12} px={2} pb={4} pt={2}>
                  <ControlledAutocompleteDropdownLazy
                    name='cause'
                    componentWidth='100%'
                    placeholder={t('member:exitMember.chooseCause')}
                    callback={(x) => ConsoleHelper.log(x, 'snurre')}
                    fetchMethod={async () => {
                      try {
                        const result = await getResCause();
                        if (isMounted()) {
                          const items = result.data.results[0].extraOptions.filter((x) => x.value !== '');
                          return items;
                        }
                      } catch (e) {
                        if (isMounted()) {
                          ConsoleHelper.log(e);
                        }
                      }

                      return [];
                    }}
                    nameProp='name'
                    label={`${t('member:exitMember.resCause')}*`}
                  />
                </Grid>
              </Grid>
              {openConfirm && (
                <MemberResignMemberConfirmDialog
                  title={t('member:exitMember.areYouSure')}
                  modaltext={t('member:exitMember.areYouSureText')}
                  open={openConfirm}
                  onClose={() => setOpenConfirm(false)}
                  onSave={onSaveConfirmHandler}
                />
              )}
            </form>
          </FormProvider>
        </>
      }
    />
  );
};
